import React, { useMemo, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, AgGridReact, Grid } from "@jsq/ds/core";
import { columnDefinitions } from "./columnDefinitions";
import { FundsGraphqlDataSource } from "./FundsGraphqlDataSource";
import { tableContainer } from "./styles_vanilla.css";
import { FundRow } from "./types";

const DEFAULT_PAGE_SIZE = 50;

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true,
  pagination: true,
  paginationPageSize: DEFAULT_PAGE_SIZE,
  paginationPageSizeSelector: false,
  suppressCellFocus: true,
  suppressContextMenu: true,
};

const defaultColumnOptions = {
  editable: false,
  flex: 1,
  minWidth: 100,
  sortable: false,
};

export interface FundsTableProps {
  /**
   * Callback function invoked when a fund row is clicked.
   * The consuming application can use this to navigate to a fund details page.
   * @param fund - The fund row data that was clicked
   */
  onFundClick?: (fund: FundRow) => void;
}

export const FundsTable: React.FC<FundsTableProps> = ({ onFundClick }) => {
  const client = useApolloClient();

  const gridDataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    return new FundsGraphqlDataSource(client);
  }, [client]);

  const onFilterChanged = useCallback((event: AgGrid.FilterChangedEvent) => {
    // Reset selection when filters change
    event.api.deselectAll();
  }, []);

  const onRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<FundRow>) => {
      const fundRowDetails = event.data;
      if (fundRowDetails && onFundClick) {
        onFundClick(fundRowDetails);
      }
    },
    [onFundClick],
  );

  return (
    <Grid<FundRow>
      cacheBlockSize={DEFAULT_PAGE_SIZE}
      className={tableContainer}
      columnDefs={columnDefinitions}
      defaultColDef={defaultColumnOptions}
      gridId="funds_table"
      gridOptions={{
        ...gridOptions,
      }}
      onFilterChanged={onFilterChanged}
      onRowClicked={onRowClicked}
      reactiveCustomComponents
      rowModelType="serverSide"
      serverSideDatasource={gridDataSource}
    />
  );
};

export default FundsTable;
